/**
 * GENERATION & IMPACT — Site-envelope evidence notices.
 *
 * The screening-evidence resolver emits one rejection per (document, fact).
 * A site with a few uploads produces six near-identical lines that outweigh
 * the inputs they annotate and never say WHY a document was set aside or WHAT
 * would make it count. This module folds those rejections into one notice per
 * document with a plain-language reason and the concrete next action.
 *
 * Pure and deterministic: same rejections → same notices. No resolver logic is
 * duplicated here — codes come from the resolver; only the wording is owned
 * here.
 */
import type { GenerationScreeningPrefillRejection } from './screeningEvidencePrefill';

export type ScreeningEvidenceNoticeSeverity = 'info' | 'action';

export interface ScreeningEvidenceNotice {
  /** Document (or SED section / benchmark) id — the part before any `:fact` suffix. */
  sourceId: string;
  /** Resolver source kind, kept for callers that deep-link into Documents. */
  source: GenerationScreeningPrefillRejection['source'];
  /** Human label extracted from the resolver message (file name when present). */
  label: string;
  /** One-line reason in plain language. */
  reason: string;
  /** Concrete thing the user can do to make the evidence count; empty when nothing. */
  nextStep: string;
  /** `action` when a user step exists; `info` for purely informational exclusions. */
  severity: ScreeningEvidenceNoticeSeverity;
  /** Fields affected (when the resolver attributed the rejection to inputs). */
  fields: readonly string[];
  /** Every underlying rejection code, deduplicated, for tooltips/tests. */
  codes: readonly GenerationScreeningPrefillRejection['code'][];
}

interface CodeCopy {
  reason: string;
  nextStep: string;
}

/**
 * Resolver code → plain reason + action. Wording is deliberately specific
 * about the gate that fired (e.g. firm-transport vs scoping estimate) so the
 * user sees the rule, not just "rejected".
 */
const CODE_COPY: Record<GenerationScreeningPrefillRejection['code'], CodeCopy> = {
  'document-excluded': {
    reason: 'Archived, deleted, superseded, unreadable, or excluded from evidence use.',
    nextStep: '',
  },
  'hash-missing': {
    reason: 'No SHA-256 on record, so its values stay review-only.',
    nextStep: 'Re-upload the file to create a verifiable lineage.',
  },
  'hash-stale': {
    reason: 'The file changed after it was screened.',
    nextStep: 'Re-run extraction on the current version.',
  },
  'extractor-stale': {
    reason: 'Screened by an older extractor version.',
    nextStep: 'Re-run the current extractor.',
  },
  'extraction-incomplete': {
    reason: 'No complete Generation screening extraction yet.',
    nextStep: 'Open it in Documents and run screening extraction.',
  },
  'fact-not-eligible': {
    reason: 'The figure was found but did not pass deterministic validation, so it is review-only.',
    nextStep: 'Confirm the number in the document and enter it manually here.',
  },
  'invalid-value': {
    reason: 'The extracted value is outside the physically valid range.',
    nextStep: 'Check the document and enter the correct figure manually.',
  },
  'unit-mismatch': {
    reason: 'The figure is in a unit the screen cannot accept without a documented conversion.',
    nextStep: 'Enter the value in the unit shown on the field.',
  },
  'technology-mismatch': {
    reason: 'Stated for a different generation technology than this scenario.',
    nextStep: '',
  },
  'cooling-mismatch': {
    reason: 'Stated for a different cooling basis than this scenario.',
    nextStep: '',
  },
  'load-basis-mismatch': {
    reason: 'Stated on a different load basis than this scenario.',
    nextStep: '',
  },
  'basis-ambiguous': {
    reason: 'The document does not state which basis the figure is on.',
    nextStep: 'Confirm the basis and enter the value manually.',
  },
  'lineage-invalid': {
    reason: 'Its provenance chain does not resolve to the current document.',
    nextStep: 'Re-upload and re-extract the file.',
  },
  'sed-section-ineligible': {
    reason: 'The SED section holds no eligible figure for this field.',
    nextStep: '',
  },
  'not-firm-site-deliverable': {
    reason: 'Describes a scoping or indicative gas quantity, not firm, site-deliverable transport.',
    nextStep: 'Enter the firm Dth/day from a transport agreement, or attach the agreement.',
  },
  'unsupported-pipeline-unit': {
    reason: 'The pipeline quantity is in a unit the screen cannot convert.',
    nextStep: 'Enter the firm capacity in Dth/day.',
  },
  'benchmark-invalid': {
    reason: 'The benchmark value failed validation.',
    nextStep: '',
  },
  'benchmark-citation-missing': {
    reason: 'The benchmark carries no citation, so it cannot be applied automatically.',
    nextStep: '',
  },
  'benchmark-not-applicable': {
    reason: 'The benchmark does not apply to this scenario.',
    nextStep: '',
  },
  'pue-required': {
    reason: 'Needs a PUE to convert to the facility basis.',
    nextStep: 'Set the PUE in Project Intent.',
  },
  'defective-feasibility-slot-quarantined': {
    reason: 'The feasibility slot it came from is quarantined as defective.',
    nextStep: '',
  },
};

/** Human-friendly field names for the affected-field list. */
const FIELD_LABELS: Record<string, string> = {
  availableGasCapacityDthPerDay: 'firm gas',
  availableWaterConsumptionGalPerDay: 'water capacity',
  ccsExportCapacityTonnesPerYear: 'CO₂ export',
  'receptors.nearest.distanceM': 'nearest receptor',
  'units.ambientFactor': 'output factor',
  'units.heatRateBtuPerKWhHhv': 'heat rate',
  'water.dataCenterConsumptionGalPerMWhFacility': 'facility water',
  'water.generationConsumptionGalPerMWhGross': 'generation water',
  'footprint.plantAcresPerGrossMW': 'plant intensity',
};

function documentKey(sourceId: string): string {
  // Per-fact rejections are keyed `${documentId}:${factId}`; collapse to the document.
  const separator = sourceId.indexOf(':');
  return separator === -1 ? sourceId : sourceId.slice(0, separator);
}

/**
 * The resolver embeds the file name at the head of every document message
 * ("Notes.docx: …" or "Notes.docx has no …"). Recover it without re-reading
 * documents so the notice can name the file even when the caller has no
 * document list.
 */
function labelFromMessage(message: string, fallback: string): string {
  const colon = message.match(/^(.+?\.[A-Za-z0-9]{1,5}):\s/);
  if (colon?.[1]) return colon[1];
  const verb = message.match(/^(.+?\.[A-Za-z0-9]{1,5})\s(?:is|has|was|changed|remains)\b/);
  if (verb?.[1]) return verb[1];
  return fallback;
}

/**
 * Fold per-fact rejections into one notice per document. Order is stable:
 * actionable notices first, then informational, each in first-seen order.
 */
export function summarizeScreeningEvidenceRejections(
  rejections: readonly GenerationScreeningPrefillRejection[],
): readonly ScreeningEvidenceNotice[] {
  const byDocument = new Map<string, {
    source: GenerationScreeningPrefillRejection['source'];
    label: string;
    codes: GenerationScreeningPrefillRejection['code'][];
    fields: Set<string>;
    firstIndex: number;
  }>();

  rejections.forEach((rejection, index) => {
    const key = `${rejection.source}:${documentKey(rejection.sourceId)}`;
    const existing = byDocument.get(key);
    if (existing) {
      if (!existing.codes.includes(rejection.code)) existing.codes.push(rejection.code);
      if (rejection.field) existing.fields.add(rejection.field);
      return;
    }
    byDocument.set(key, {
      source: rejection.source,
      label: labelFromMessage(rejection.message, documentKey(rejection.sourceId)),
      codes: [rejection.code],
      fields: new Set(rejection.field ? [rejection.field] : []),
      firstIndex: index,
    });
  });

  const notices: ScreeningEvidenceNotice[] = [];
  for (const [key, entry] of byDocument) {
    // The first code is the one that stopped the document; later codes are
    // per-fact details. Lead with the first, append distinct next steps.
    const copies = entry.codes.map(code => CODE_COPY[code]);
    const reason = copies[0]?.reason ?? 'Not eligible for automatic use.';
    const nextStep = [...new Set(copies.map(copy => copy.nextStep).filter(Boolean))].join(' ');
    notices.push({
      sourceId: key.slice(key.indexOf(':') + 1),
      source: entry.source,
      label: entry.label,
      reason,
      nextStep,
      severity: nextStep ? 'action' : 'info',
      fields: [...entry.fields].map(field => FIELD_LABELS[field] ?? field),
      codes: entry.codes,
    });
  }

  return notices.sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'action' ? -1 : 1;
    return 0;
  });
}
